import './StakeholdersSectionCard.scss';
import { ReactElement, useState } from 'react';
import { InternalStakeholder } from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { InteractiveTr } from '@Components/InteractiveEl';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useNotImplemented } from '@src/utils/hooks/useNotImplemented';
import { useInternalStakeholders } from '../useInternalStakeholders';
import { EditTeamMemberModal } from './EditTeamMemberModal';
import { SectionCard } from './SectionCard';
import { SectionCardBody } from './SectionCardBody';

const EMPTY_STATE_COPY = 'No team members are mapped to this client yet.';
const ERROR_STATE_COPY = "Couldn't load team members.";

interface StakeholdersSectionCardProps {
  icon: ReactElement;
  label: string;
}

export const StakeholdersSectionCard = ({ icon, label }: StakeholdersSectionCardProps) => {
  const { cid } = useCompanyInfo();
  const { data, isLoading, isError } = useInternalStakeholders(cid);
  const stakeholders = data?.stakeholders ?? [];
  const [editing, setEditing] = useState<InternalStakeholder | null>(null);
  const notImplemented = useNotImplemented();

  return (
    <SectionCard icon={icon} label={label} className='stakeholders-section-card'>
      <SectionCardBody
        isLoading={isLoading}
        isError={isError}
        isEmpty={stakeholders.length === 0}
        errorCopy={ERROR_STATE_COPY}
        emptyCopy={EMPTY_STATE_COPY}
      >
        <StakeholdersTable stakeholders={stakeholders} onEdit={setEditing} />
      </SectionCardBody>
      <EditTeamMemberModal
        stakeholder={editing}
        onHide={() => setEditing(null)}
        onSave={notImplemented}
      />
    </SectionCard>
  );
};

interface StakeholdersTableProps {
  stakeholders: InternalStakeholder[];
  onEdit: (stakeholder: InternalStakeholder) => void;
}

const StakeholdersTable = ({ stakeholders, onEdit }: StakeholdersTableProps) => (
  <div className='stakeholders-table-wrapper'>
    <table className='stakeholders-table'>
      <thead>
        <tr>
          <th>Team member</th>
          <th>Title</th>
          <th>Role on account</th>
        </tr>
      </thead>
      <tbody>
        {stakeholders.map(stakeholder => (
          <StakeholderRow key={stakeholder.email} stakeholder={stakeholder} onEdit={onEdit} />
        ))}
      </tbody>
    </table>
  </div>
);

interface StakeholderRowProps {
  stakeholder: InternalStakeholder;
  onEdit: (stakeholder: InternalStakeholder) => void;
}

const StakeholderRow = ({ stakeholder, onEdit }: StakeholderRowProps) => {
  const { name, pronouns, title, roleOnAccount } = stakeholder;

  return (
    <InteractiveTr
      className='stakeholders-row'
      aria-label={`Edit ${name}`}
      aria-haspopup='dialog'
      onClick={() => onEdit(stakeholder)}
    >
      <td>
        {name}
        {pronouns && <span className='stakeholders-pronouns'>({pronouns})</span>}
      </td>
      <td>{title}</td>
      <td>{roleOnAccount}</td>
    </InteractiveTr>
  );
};
